import { Dispatch } from 'redux';
import { recordNpsPopupShown, submitNpsResponse, getLastNpsShown } from '../api/Nps/npsApi';
import {
  IRecordNpsPopupShownRequest,
  ISubmitNpsRequest,
  NpsTriggerPoint,
  NpsFrequency,
} from '../types/nps';
import { NPS_DWELL_TIME_MS, NPS_FREQUENCY_DAYS } from '../constants/npsStrings';

export const NPS_OPEN_MODAL = 'NPS_OPEN_MODAL';
export const NPS_SELECT_SCORE = 'NPS_SELECT_SCORE';
export const NPS_RECORD_SHOWN_SUCCESS = 'NPS_RECORD_SHOWN_SUCCESS';
export const NPS_RECORD_SHOWN_FAILURE = 'NPS_RECORD_SHOWN_FAILURE';
export const NPS_SUBMIT_REQUEST = 'NPS_SUBMIT_REQUEST';
export const NPS_SUBMIT_SUCCESS = 'NPS_SUBMIT_SUCCESS';
export const NPS_SUBMIT_FAILURE = 'NPS_SUBMIT_FAILURE';

const NPS_DWELL_START_KEY = 'nps_dwell_start';
let checkInProgress = false;

const getDwellStart = (): number => {
  const stored = sessionStorage.getItem(NPS_DWELL_START_KEY);
  return stored ? Number(stored) : 0;
};

export const startNpsDwellTimer = () => {
  sessionStorage.setItem(NPS_DWELL_START_KEY, Date.now().toString());
};

export const openNpsModal = () => ({ type: NPS_OPEN_MODAL });

export const selectNpsScore = (score: number) => ({
  type: NPS_SELECT_SCORE,
  payload: score,
});

/**
 * Checks config, dwell time, and frequency via API, then shows popup if eligible.
 */
export const tryShowNps = (triggerPoint: string) => {
  return async (dispatch: Dispatch, getState: () => any) => {
    if (checkInProgress) {
      return;
    }

    const state = getState();
    const retailerData = state.retailerDetails && state.retailerDetails.data;
    const userDetails = state.userDetails;
    const meta = (retailerData && retailerData.meta) || {};
    const userId = (userDetails && userDetails.userId > 0) ? userDetails.userId : 0;
    const retailerId = (retailerData && retailerData.id) || 0;

    const isManual = triggerPoint === NpsTriggerPoint.Manual;

    if (meta.NpsEnabled !== 'true' || userId === 0) {
      return;
    }

    if (state.nps.isModalOpen) {
      return;
    }

    if (!isManual) {
      const frequency: NpsFrequency = meta.NpsFrequency || 'Never';
      if (frequency === 'Never') {
        return;
      }

      const configuredTrigger = meta.NpsTrigger || 'AfterOrder';
      if (triggerPoint.toLowerCase() !== configuredTrigger.toLowerCase()) {
        return;
      }

      const dwellStart = getDwellStart();
      if (dwellStart === 0 || Date.now() - dwellStart < NPS_DWELL_TIME_MS) {
        return;
      }

      checkInProgress = true;
      try {
        const response = await getLastNpsShown();
        if (response.lastShownAt) {
          const days = NPS_FREQUENCY_DAYS[frequency] || Infinity;
          const elapsed =
            (Date.now() - new Date(response.lastShownAt).getTime()) / (1000 * 60 * 60 * 24);
          if (elapsed < days) {
            return;
          }
        }
      } catch (_err) {
        // noop
      } finally {
        checkInProgress = false;
      }
    }

    (dispatch as any)(recordNpsShown({ triggerPoint: triggerPoint as NpsTriggerPoint }));
    dispatch(openNpsModal());
  };
};

export const recordNpsShown = (data: IRecordNpsPopupShownRequest) => {
  return async (dispatch: Dispatch) => {
    try {
      const response = await recordNpsPopupShown(data);
      dispatch({
        type: NPS_RECORD_SHOWN_SUCCESS,
        payload: response.npsResponseId,
      });
    } catch (error) {
      dispatch({ type: NPS_RECORD_SHOWN_FAILURE });
    }
  };
};

export const submitNps = (data: ISubmitNpsRequest) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: NPS_SUBMIT_REQUEST });
    try {
      await submitNpsResponse(data);
      dispatch({ type: NPS_SUBMIT_SUCCESS });
    } catch (error) {
      dispatch({
        type: NPS_SUBMIT_FAILURE,
        payload: 'Failed to submit NPS response.',
      });
    }
  };
};
